'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import {
  changePasswordSchema,
  type IChangePassword,
  useSession,
  useShipperLoginApi,
} from '@repo/sdk'
import { Button, Input, Spinner } from '@repo/ui'
import { toast } from '@repo/ui'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@repo/ui/components'
import { useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { EditUserData } from './user-edit.tsx'

export const AccountSettings = () => {
  const session = useSession()

  const userId = session?.data?.user?.id

  const form = useForm<IChangePassword>({
    resolver: zodResolver(changePasswordSchema),
  })

  const { changeUserPassword } = useShipperLoginApi()

  const { isPending, mutate } = useMutation({
    mutationFn: async (mutationData: IChangePassword) => {
      return changeUserPassword(z.coerce.number().optional().parse(userId), {
        password: mutationData.password,
        password_confirmation: mutationData.confirmPassword,
      })
    },
    onError: error => {
      toast.error(error.message)
    },
    onSuccess: () => {
      toast.success('Password changed successfully')
      form.reset()
    },
  })

  return (
    <div className={'mx-auto mb-[60px] w-[90%] max-w-[968px] py-6 dark:text-gray-300'}>
      <EditUserData />
      <div className={'mt-8 border-b border-b-gray-200 pb-4'}>
        <p className={'text-lg font-medium md:text-2xl'}>Change password</p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(value => mutate(value))}>
          <div className={'grid grid-cols-1 gap-2 py-4 xl:grid-cols-2 xl:gap-6'}>
            <FormField
              control={form.control}
              name={'password'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input {...field} placeholder="Current password" type="password" />
                  </FormControl>
                  <FormMessage className={'whitespace-pre-line'} />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'confirmPassword'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input {...field} placeholder="Confirm new password" type="password" />
                  </FormControl>
                  <FormMessage className={'whitespace-pre-line'} />
                </FormItem>
              )}
            />
          </div>
          <div className={'flex justify-end'}>
            <Button className={'mb-6 h-[50px] font-semibold'} disabled={isPending} type="submit">
              Save {isPending && <Spinner />}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
